import { Loader2, ShieldCheck } from "lucide-react";
import { useMemo } from "react";
import { useLocale, useTranslations } from "use-intl";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { AuditEntry, AuditListFilter } from "@/types/audit-log.types";
import { cn } from "@/utils";
import { formatRelativeTime } from "./format-relative-time";
import { renderResourceAuditEntry } from "./render-resource-audit-entry";
import { useResourceAuditQuery } from "./use-resource-audit-query";

interface ResourceAuditListProps {
  resourceType: string;
  resourceId: string;
  filter?: Omit<AuditListFilter, "resourceType" | "resourceId" | "page" | "limit">;
  compact?: boolean;
  limit?: number;
  framed?: boolean;
}

export function ResourceAuditList({
  resourceType,
  resourceId,
  filter,
  compact,
  limit,
  framed,
}: ResourceAuditListProps) {
  const t = useTranslations("resource_activity");
  const rootT = useTranslations();
  const locale = useLocale();
  const query = useResourceAuditQuery({ resourceType, resourceId, filter, limit });
  const rows = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data]);

  if (query.isLoading) {
    return <ListSkeleton rows={compact ? 3 : 5} />;
  }
  if (query.error) {
    return <p className="py-8 text-center text-sm text-red-500">{t("audit.load_failed")}</p>;
  }
  if (rows.length === 0) {
    return <p className="py-8 text-center text-sm text-[var(--muted-foreground)]">{t("audit.empty")}</p>;
  }

  const content = (
    <div className="space-y-3">
      <ol className={cn("relative border-l border-[var(--border)]", compact ? "ml-2 pl-4" : "ml-3 pl-5")}>
        {rows.map((entry) => (
          <AuditRow key={entry.id} entry={entry} label={renderResourceAuditEntry(entry, rootT)} locale={locale} />
        ))}
      </ol>
      {query.hasNextPage && !compact && (
        <div className="flex justify-center border-t border-[var(--border)] pt-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? <Loader2 className="size-4 animate-spin" /> : null}
            {query.isFetchingNextPage ? t("loading_more") : t("load_more")}
          </Button>
        </div>
      )}
    </div>
  );

  if (!framed) return content;

  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2">
      {content}
    </div>
  );
}

function AuditRow({ entry, label, locale }: { entry: AuditEntry; label: string; locale: string }) {
  return (
    <li className="relative mb-3 min-w-0 rounded-md px-2 py-1.5">
      <span className="absolute -left-[22px] top-3 flex size-5 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--card)]">
        <ShieldCheck className="size-3 text-[var(--muted-foreground)]" aria-hidden />
      </span>
      <p className="line-clamp-2 text-sm text-[var(--foreground)]">{label}</p>
      <p className="mt-0.5 text-xs text-[var(--muted-foreground)]">{formatRelativeTime(entry.createdAt, locale)}</p>
    </li>
  );
}

function ListSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex items-center gap-3">
          <Skeleton className="size-7 rounded-full" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-4/5 rounded" />
            <Skeleton className="h-3 w-24 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}
